import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, ListOrdered, Users, Clapperboard, FastForward } from "lucide-react";
import { AccountMenu } from "@/components/AccountMenu";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScriptTab } from "@/components/studio/ScriptTab";
import { ScenesTab } from "@/components/studio/ScenesTab";
import { CastTab } from "@/components/studio/CastTab";
import { StudioTab } from "@/components/studio/StudioTab";
import { ExportMenu } from "@/components/studio/ExportMenu";
import { MoveToLive } from "@/components/studio/MoveToLive";

const fmt = (s) => { s = Math.round(s); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

export default function EpisodeWorkspace() {
  const { id } = useParams();
  const [ep, setEp] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [chars, setChars] = useState([]);
  const [tab, setTab] = useState("script");

  const reload = useCallback(async () => {
    try {
      const [e, s, c] = await Promise.all([
        http.get(`/episodes/${id}`), http.get(`/episodes/${id}/scenes`), http.get(`/episodes/${id}/characters`),
      ]);
      setEp(e.data); setScenes(s.data); setChars(c.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const busy = ep?.split_status === "running" || ep?.full_status === "running" || scenes.some((s) => s.status === "generating") || chars.some((c) => c.portrait_status === "generating");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [busy, reload]);

  if (!ep) return <div className="p-10 text-slate-500 font-mono text-sm">Loading…</div>;
  const approvedSecs = scenes.filter((s) => s.status === "approved").reduce((n, s) => n + (s.duration || 20), 0);
  const totalSecs = scenes.reduce((n, s) => n + (s.duration || 20), 0);
  const ctx = { ep, scenes, chars, reload, setTab };

  return (
    <div className="min-h-screen flex flex-col" data-testid="episode-workspace">
      <header className="sticky top-0 z-40 h-14 bg-[#11141E]/90 backdrop-blur-md border-b border-[#2A3245] px-4 md:px-6 flex items-center gap-4">
        <Link to="/" data-testid="back-to-episodes" className="text-slate-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
        <div className="font-display font-semibold truncate" data-testid="episode-title">{ep.title}</div>
        <div className="ml-auto flex items-center gap-5 font-mono text-xs">
          <span className="text-slate-400 hidden sm:inline" data-testid="scene-count">{scenes.length} SCENES</span>
          <span className="text-amber-400" data-testid="approved-minutes">{fmt(approvedSecs)} / {fmt(totalSecs)} APPROVED</span>
          <ExportMenu ep={ep} />
          <MoveToLive ep={ep} scenes={scenes} />
          <Link to={`/?continue=${ep.id}`} data-testid="next-episode-btn"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-amber-500 text-black text-xs font-medium hover:bg-amber-400 transition-colors">
            <FastForward className="w-3.5 h-3.5" /><span className="hidden sm:inline">Next episode</span>
          </Link>
          <AccountMenu />
        </div>
      </header>
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
        <div className="px-4 md:px-6 pt-5 overflow-x-auto">
          <TabsList className="bg-[#11141E] border border-[#2A3245]">
            <TabsTrigger value="script" data-testid="tab-script"><FileText className="w-4 h-4 mr-2" />Script</TabsTrigger>
            <TabsTrigger value="scenes" data-testid="tab-scenes"><ListOrdered className="w-4 h-4 mr-2" />Scenes</TabsTrigger>
            <TabsTrigger value="cast" data-testid="tab-cast"><Users className="w-4 h-4 mr-2" />Cast</TabsTrigger>
            <TabsTrigger value="studio" data-testid="tab-studio"><Clapperboard className="w-4 h-4 mr-2" />Studio</TabsTrigger>
          </TabsList>
        </div>
        <div className="flex-1 px-4 md:px-6 py-6">
          <TabsContent value="script" className="mt-0"><ScriptTab {...ctx} /></TabsContent>
          <TabsContent value="scenes" className="mt-0"><ScenesTab {...ctx} /></TabsContent>
          <TabsContent value="cast" className="mt-0"><CastTab {...ctx} /></TabsContent>
          <TabsContent value="studio" className="mt-0"><StudioTab {...ctx} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
